import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiImage, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import AdminPhotoUpload from '../components/AdminPhotoUpload';
import CustomModal from '../components/CustomModal';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import { useI18n } from '../utilities/i18n';
import { getApiErrorMessage } from '../utilities/stadiumsApi';
import {
  addSocialSponsor,
  compressDataUrl,
  deleteSocialSponsor,
  fetchSocialSponsorsPool,
} from '../utilities/socialPicturesApi';
import { normalizePoolItem } from '../utilities/sponsorPool';
import '../styles/stadiumsManagement.css';
import '../styles/tournamentsManagement.css';
import '../styles/sponsorsManagement.css';

const emptyForm = () => ({
  name: '',
  imageData: '',
});

export default function SponsorsManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();

  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [photoError, setPhotoError] = useState('');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const searchInputRef = useRef(null);

  const loadList = async () => {
    setLoading(true);
    setMessage({ text: '', type: '' });
    try {
      const list = await fetchSocialSponsorsPool(dispatch);
      setItems((list || []).map(normalizePoolItem).filter((item) => item.key || item.id));
    } catch (error) {
      setItems([]);
      setMessage({ text: getApiErrorMessage(error, t('spLoadError')), type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadList();
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
      const name = String(item.name || '').toLowerCase();
      const key = String(item.key || '').toLowerCase();
      return name.includes(q) || key.includes(q);
    });
  }, [items, searchQuery]);

  const openForm = () => {
    setForm(emptyForm());
    setPhotoError('');
    setShowForm(true);
    setMessage({ text: '', type: '' });
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setForm(emptyForm());
    setPhotoError('');
  };

  const handleSave = async () => {
    const name = String(form.name || '').trim();
    if (!name) {
      setMessage({ text: t('spNameRequired'), type: 'error' });
      return;
    }
    if (!form.imageData) {
      setMessage({ text: t('spImageRequired'), type: 'error' });
      return;
    }
    setSaving(true);
    setMessage({ text: '', type: '' });
    try {
      const imageData = await compressDataUrl(form.imageData);
      await addSocialSponsor(dispatch, { name, imageData });
      setShowForm(false);
      setForm(emptyForm());
      setMessage({ text: t('spAdded'), type: 'success' });
      await loadList();
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('spAddError')), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const askDelete = (item) => {
    if (!item || deletingId) return;
    setDeleteTarget(item);
  };

  const closeDeleteModal = () => {
    if (deletingId) return;
    setDeleteTarget(null);
  };

  const confirmDelete = async () => {
    const item = deleteTarget;
    if (!item || deletingId) return;
    setDeletingId(item.id != null ? item.id : item.key);
    setMessage({ text: '', type: '' });
    try {
      await deleteSocialSponsor(dispatch, {
        id: item.id,
        key: item.key,
      });
      setDeleteTarget(null);
      setMessage({ text: t('spDeleted'), type: 'success' });
      await loadList();
    } catch (error) {
      setMessage({ text: getApiErrorMessage(error, t('spDeleteError')), type: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const columns = [
    {
      key: 'image',
      label: t('spImage'),
      className: 'sp-cell-image',
      render: (item) => (
        <span className="sp-thumb-wrap">
          {item.src || item.preview ? (
            <img className="sp-thumb" src={item.src || item.preview} alt={item.name || ''} />
          ) : (
            <span className="sp-thumb sp-thumb-empty"><FiImage /></span>
          )}
        </span>
      ),
    },
    {
      key: 'name',
      label: t('spName'),
      className: 'st-cell-name sp-cell-name',
      render: (item) => (
        <span className="std-stadium-main">
          <span className="std-stadium-name">{item.name || '—'}</span>
          {item.key && item.key !== item.name ? (
            <small className="std-stadium-meta">{item.key}</small>
          ) : null}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      className: 'sp-cell-actions',
      render: (item) => (
        <button
          type="button"
          className="sp-delete-btn"
          title={t('spDelete')}
          disabled={deletingId != null}
          onClick={(event) => {
            event.stopPropagation();
            askDelete(item);
          }}
        >
          {deletingId === item.id || deletingId === item.key
            ? <Loader type="TailSpin" color="#ff6b6b" height={16} width={16} />
            : <FiTrash2 />}
        </button>
      ),
    },
  ];

  const deleteLabel = deleteTarget
    ? (deleteTarget.name || deleteTarget.key || deleteTarget.id)
    : '';

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page std-list-page sp-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs
              items={[
                { label: t('navInfographics'), to: '/infographics' },
                { label: t('navSponsors') },
              ]}
            />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              className="st-search-input"
              placeholder={t('spSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="st-meta">
            <strong>{t('spFound', { count: filtered.length })}</strong>
          </div>
          <button
            type="button"
            className="st-btn st-btn-primary sp-toolbar-add"
            disabled={showForm}
            onClick={openForm}
          >
            <FiPlus />
            <span className="tr-btn-full">{t('spAdd')}</span>
            <span className="tr-btn-short">{t('spAddShort')}</span>
          </button>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-table-wrap">
            {loading ? (
              <div className="st-loading">
                <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                <p>{t('spLoading')}</p>
              </div>
            ) : (
              <ReorderableTable
                tableClassName="sp-table"
                columns={columns}
                items={filtered}
                getRowKey={(item) => item.id != null ? item.id : item.key}
                reorderMode={false}
                empty={(
                  <div className="st-empty">
                    <FiImage size={48} />
                    <h3>{t('spEmpty')}</h3>
                    <p>{t('spEmptyHint')}</p>
                    <button
                      type="button"
                      className="st-btn st-btn-primary"
                      onClick={openForm}
                    >
                      <FiPlus /> {t('spAdd')}
                    </button>
                  </div>
                )}
              />
            )}
          </div>
        </div>

        <div className="st-actions st-actions-split">
          <Link to="/infographics" className="st-btn st-btn-secondary">
            {t('spBackToInfographics')}
          </Link>
          <button
            type="button"
            className="st-btn st-btn-primary"
            disabled={showForm}
            onClick={openForm}
          >
            <FiPlus />
            <span className="tr-btn-full">{t('spAdd')}</span>
            <span className="tr-btn-short">{t('spAddShort')}</span>
          </button>
        </div>
      </div>

      <CustomModal
        styles={{ width: 520, height: 'auto', paddingLeft: 28, paddingRight: 28, paddingTop: 20, paddingBottom: 24 }}
        visible={showForm}
        closeModal={closeForm}
        className="sp-form-modal-host"
      >
        <div className="sp-form-modal">
          <div className="sp-form-header">
            <h3>{t('spAddTitle')}</h3>
            <button type="button" className="sp-form-close" onClick={closeForm} disabled={saving}>
              <FiX />
            </button>
          </div>
          <div className="sp-form-body">
            <div className="sp-form-photo">
              <AdminPhotoUpload
                preview={form.imageData}
                error={photoError}
                disabled={saving}
                onFileChange={(file, error, dataUrl) => {
                  if (error) {
                    setPhotoError(error);
                    setForm((prev) => ({ ...prev, imageData: '' }));
                    return;
                  }
                  setPhotoError('');
                  setForm((prev) => ({ ...prev, imageData: dataUrl || '' }));
                }}
              />
            </div>
            <div className="sp-form-fields">
              <label className="sp-field-label" htmlFor="sp-name">{t('spName')}</label>
              <input
                id="sp-name"
                className="st-search-input sp-name-input"
                type="text"
                value={form.name}
                disabled={saving}
                maxLength={80}
                placeholder={t('spNamePh')}
                onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
              />
              <div className="sp-form-actions">
                <button
                  type="button"
                  className="st-btn st-btn-secondary"
                  disabled={saving}
                  onClick={closeForm}
                >
                  {t('cancel')}
                </button>
                <button
                  type="button"
                  className="st-btn st-btn-primary"
                  disabled={saving || !form.name.trim() || !form.imageData}
                  onClick={handleSave}
                >
                  {saving
                    ? <Loader type="TailSpin" color="#101510" height={18} width={18} />
                    : <FiPlus />}
                  {saving ? t('spSaving') : t('spAdd')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </CustomModal>

      <CustomModal
        styles={{ width: 420, height: 'auto', paddingLeft: 32, paddingRight: 32 }}
        visible={!!deleteTarget}
        closeModal={closeDeleteModal}
      >
        <div className="st-delete-modal">
          <div className="customModalHeader">{t('spDeleteTitle')}</div>
          <p>{t('spDeleteText', { name: deleteLabel })}</p>
          <div className="st-delete-actions">
            <button
              type="button"
              className="btn-modal btn-secondary-modal"
              disabled={deletingId != null}
              onClick={closeDeleteModal}
            >
              {t('cancel')}
            </button>
            <button
              type="button"
              className="btn-modal btn-danger-modal"
              disabled={deletingId != null}
              onClick={confirmDelete}
            >
              {deletingId != null
                ? <Loader type="TailSpin" color="#fff" height={16} width={16} />
                : t('delete')}
            </button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
